import { useState, useRef, useEffect } from "react";
import { useQuery, useMutation, useQueryClient as useQueryClientHook } from "@tanstack/react-query";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { authFetch } from "@/lib/authFetch";
import {
  Plug, RefreshCw, Loader2, CheckCircle2, AlertTriangle,
  ExternalLink, Bot, ShoppingCart, WifiOff, Wifi, FileText, Image,
  Activity, ChevronDown, ChevronUp, XCircle, Sparkles, CheckCircle, Clock, KeyRound,
} from "lucide-react";

// ── Types ────────────────────────────────────────────────────────────────────

interface ZohoStatus {
  connected: boolean;
  expiresAt?: string;
  updatedAt?: string;
}

interface CommandesStats {
  total: number;
  totalMois: number;
  caMoisHt: number;
  enAttente: number;
  clientsMois: number;
}

interface ZohoSyncResult {
  imported: number;
  updated: number;
  errors: number;
  total: number;
  facturesImported: number;
  facturesUpdated: number;
  factureErrors: number;
  totalFactures: number;
}

interface FacturesStats {
  total: number;
  derniereSync: string | null;
}

interface PuduStatus {
  connected: boolean;
}

interface PuduSyncStatus {
  id?: string;
  status: "never" | "running" | "completed" | "error" | "already_running";
  started_at?: string;
  completed_at?: string;
  shops_synced?: number;
  robots_synced?: number;
  resellers_matched?: number;
  error_message?: string;
  message?: string;
  syncId?: string;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function StatusBadge({ connected }: { connected: boolean }) {
  return connected ? (
    <Badge className="gap-1 bg-green-100 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-400 dark:border-green-800">
      <Wifi className="w-3 h-3" />
      Connecté
    </Badge>
  ) : (
    <Badge variant="outline" className="gap-1 text-muted-foreground">
      <WifiOff className="w-3 h-3" />
      Non connecté
    </Badge>
  );
}

function IntegrationCard({ children }: { children: React.ReactNode }) {
  return (
    <div className="bg-card border rounded-xl shadow-sm overflow-hidden">
      {children}
    </div>
  );
}

// ── Zoho Inventory Card ───────────────────────────────────────────────────────

function ZohoCard() {
  const { toast } = useToast();
  const [syncResult, setSyncResult] = useState<ZohoSyncResult | null>(null);

  const { data: zohoStatus, isLoading: statusLoading } = useQuery<ZohoStatus>({
    queryKey: ["/api/zoho/status"],
    queryFn: () => authFetch("/api/zoho/status").then(r => r.json()),
    refetchInterval: 30_000,
  });

  const { data: commandesStats } = useQuery<CommandesStats>({
    queryKey: ["/api/commandes/stats"],
  });

  const zohoSync = useMutation({
    mutationFn: async () => {
      const res = await authFetch("/api/zoho/sync", { method: "POST" });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || `Erreur serveur ${res.status}`);
      return data as ZohoSyncResult;
    },
    onSuccess: (data) => {
      setSyncResult(data);
      toast({ title: "Synchronisation Zoho terminée", description: `${data.imported} importées, ${data.updated} mises à jour` });
    },
    onError: (err: any) => toast({ title: "Erreur Zoho", description: err.message, variant: "destructive" }),
  });

  const connected = zohoStatus?.connected ?? false;

  const lastSync = zohoStatus?.updatedAt
    ? new Intl.DateTimeFormat("fr-FR", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }).format(new Date(zohoStatus.updatedAt))
    : null;

  return (
    <IntegrationCard>
      <div className="p-6 space-y-5">
        {/* Header */}
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-[#E42527]/10 flex items-center justify-center shrink-0">
              <ShoppingCart className="w-6 h-6 text-[#E42527]" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-semibold text-base">Zoho Inventory</h3>
                {!statusLoading && <StatusBadge connected={connected} />}
              </div>
              <p className="text-sm text-muted-foreground mt-0.5">
                Synchronisation des commandes clients, revendeurs et achats fournisseur Pudu
              </p>
            </div>
          </div>
        </div>

        {/* Stats */}
        {commandesStats && (
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-secondary/40 rounded-lg p-3 text-center">
              <p className="text-xl font-bold">{commandesStats.total}</p>
              <p className="text-xs text-muted-foreground">Commandes totales</p>
            </div>
            <div className="bg-secondary/40 rounded-lg p-3 text-center">
              <p className="text-xl font-bold">{commandesStats.totalMois}</p>
              <p className="text-xs text-muted-foreground">Ce mois-ci</p>
            </div>
            <div className="bg-secondary/40 rounded-lg p-3 text-center">
              <p className="text-xl font-bold">{commandesStats.enAttente}</p>
              <p className="text-xs text-muted-foreground">En attente</p>
            </div>
          </div>
        )}

        {lastSync && (
          <p className="text-xs text-muted-foreground">
            Dernière synchronisation : <span className="font-medium text-foreground">{lastSync}</span>
          </p>
        )}

        {/* Error inline */}
        {zohoSync.isError && (
          <div className="flex items-start gap-2 bg-destructive/10 border border-destructive/30 rounded-lg px-4 py-3" data-testid="error-zoho-sync">
            <AlertTriangle className="w-4 h-4 text-destructive shrink-0 mt-0.5" />
            <p className="text-sm text-destructive">{(zohoSync.error as any)?.message}</p>
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-wrap gap-2">
          <Button
            onClick={() => zohoSync.mutate()}
            disabled={zohoSync.isPending || !connected}
            data-testid="button-zoho-sync"
            className="gap-2"
          >
            {zohoSync.isPending ? (
              <><Loader2 className="w-4 h-4 animate-spin" />Synchronisation en cours…</>
            ) : (
              <><RefreshCw className="w-4 h-4" />Synchroniser (Commandes & Factures)</>
            )}
          </Button>

          <Button
            variant="outline"
            onClick={() => { window.location.href = "/api/zoho/auth"; }}
            data-testid="button-zoho-reconnect"
            className="gap-2"
          >
            <ExternalLink className="w-4 h-4" />
            {connected ? "Reconnecter" : "Connecter"}
          </Button>
        </div>

        {/* Sync result */}
        {syncResult && (
          <div className="border rounded-lg p-4 bg-green-50 dark:bg-green-950/20 space-y-3">
            <div className="flex items-center gap-2 text-green-700 dark:text-green-400 font-medium text-sm">
              <CheckCircle2 className="w-4 h-4" />
              Résultat de la synchronisation
            </div>
            <div>
              <p className="text-xs font-medium text-muted-foreground mb-1.5">Commandes (Inventory)</p>
              <div className="grid grid-cols-3 gap-2 text-center text-sm">
                <div>
                  <p className="font-bold text-green-700 dark:text-green-400">{syncResult.imported}</p>
                  <p className="text-xs text-muted-foreground">Importées</p>
                </div>
                <div>
                  <p className="font-bold text-blue-600">{syncResult.updated}</p>
                  <p className="text-xs text-muted-foreground">Mises à jour</p>
                </div>
                <div>
                  <p className="font-bold text-amber-600">{syncResult.errors}</p>
                  <p className="text-xs text-muted-foreground">Erreurs</p>
                </div>
              </div>
            </div>
            <div>
              <p className="text-xs font-medium text-muted-foreground mb-1.5">Factures (Books)</p>
              <div className="grid grid-cols-2 gap-2 text-center text-sm">
                <div>
                  <p className="font-bold text-green-700 dark:text-green-400">{syncResult.totalFactures}</p>
                  <p className="text-xs text-muted-foreground">Synchronisées</p>
                </div>
                <div>
                  <p className="font-bold text-amber-600">{syncResult.factureErrors}</p>
                  <p className="text-xs text-muted-foreground">Erreurs</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </IntegrationCard>
  );
}

// ── Zoho Billing Card ─────────────────────────────────────────────────────────

interface BillingSyncStatus {
  running: boolean;
  imported: number;
  errors: number;
  startedAt: string | null;
  finishedAt: string | null;
  lastError: string | null;
}

function ZohoBillingCard() {
  const { toast } = useToast();
  const queryClient = useQueryClientHook();
  const [pollEnabled, setPollEnabled] = useState(false);

  const { data: stats, refetch: refetchStats } = useQuery<FacturesStats>({
    queryKey: ["/api/factures/stats"],
    queryFn: () => authFetch("/api/factures/stats").then(r => r.json()),
    refetchInterval: pollEnabled ? 5_000 : 30_000,
  });

  const { data: zohoStatus } = useQuery<ZohoStatus>({
    queryKey: ["/api/zoho/status"],
    queryFn: () => authFetch("/api/zoho/status").then(r => r.json()),
  });

  const { data: syncStatus } = useQuery<BillingSyncStatus>({
    queryKey: ["/api/zoho/sync-billing/status"],
    queryFn: () => authFetch("/api/zoho/sync-billing/status").then(r => r.json()),
    refetchInterval: pollEnabled ? 3_000 : false,
  });

  // When sync finishes, stop polling and refresh stats
  const prevRunning = useRef(false);
  useEffect(() => {
    if (prevRunning.current && syncStatus && !syncStatus.running) {
      setPollEnabled(false);
      refetchStats();
      queryClient.invalidateQueries({ queryKey: ["/api/factures/stats"] });
      if (syncStatus.lastError) {
        toast({ title: "Erreur sync Billing", description: syncStatus.lastError, variant: "destructive" });
      } else {
        toast({ title: "Sync Billing terminée", description: `${syncStatus.imported} factures importées, ${syncStatus.errors} erreur(s)` });
      }
    }
    prevRunning.current = syncStatus?.running ?? false;
  }, [syncStatus]);

  const syncMutation = useMutation({
    mutationFn: async () => {
      const res = await authFetch("/api/zoho/sync-billing", { method: "POST" });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || `Erreur ${res.status}`);
      return data;
    },
    onSuccess: (data) => {
      if (data.status === "already_running") {
        toast({ title: "Sync déjà en cours", description: `${data.imported ?? 0} factures importées jusqu'ici` });
      } else {
        setPollEnabled(true);
        toast({ title: "Sync démarrée", description: "Synchronisation des factures en arrière-plan…" });
      }
    },
    onError: (e: any) => toast({ title: "Erreur", description: e.message, variant: "destructive" }),
  });

  const connected = zohoStatus?.connected ?? false;
  const isRunning = syncStatus?.running ?? false;

  const lastSync = stats?.derniereSync
    ? new Intl.DateTimeFormat("fr-FR", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }).format(new Date(stats.derniereSync))
    : null;

  return (
    <IntegrationCard>
      <div className="p-6 space-y-5">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-blue-500/10 flex items-center justify-center shrink-0">
              <FileText className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-semibold text-base">Zoho Billing</h3>
                <StatusBadge connected={connected} />
              </div>
              <p className="text-sm text-muted-foreground mt-0.5">
                Factures clients synchronisées depuis Zoho Billing
              </p>
            </div>
          </div>
        </div>

        {isRunning && (
          <div className="flex items-center gap-2 text-sm text-blue-600 bg-blue-50 dark:bg-blue-950/30 rounded-lg px-3 py-2">
            <RefreshCw className="w-4 h-4 animate-spin" />
            <span>Sync en cours… {syncStatus?.imported ?? 0} factures importées</span>
          </div>
        )}

        {stats && (
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-secondary/40 rounded-lg p-3 text-center">
              <p className="text-xl font-bold">{stats.total}</p>
              <p className="text-xs text-muted-foreground">Factures synchronisées</p>
            </div>
            <div className="bg-secondary/40 rounded-lg p-3 text-center">
              <p className="text-sm font-medium">{lastSync ?? "—"}</p>
              <p className="text-xs text-muted-foreground">Dernière sync</p>
            </div>
          </div>
        )}

        <div className="flex gap-2 flex-wrap">
          <Button
            variant="outline"
            onClick={() => syncMutation.mutate()}
            disabled={isRunning || syncMutation.isPending}
            data-testid="button-zoho-billing-sync"
            className="gap-2"
          >
            <RefreshCw className={`w-4 h-4 ${isRunning ? "animate-spin" : ""}`} />
            {isRunning ? "Sync en cours…" : "Synchroniser"}
          </Button>
          <Button
            variant="ghost"
            onClick={() => { window.location.href = "/api/zoho/auth"; }}
            data-testid="button-zoho-billing-reconnect"
            className="gap-2 text-muted-foreground"
          >
            <ExternalLink className="w-4 h-4" />
            {connected ? "Reconnecter" : "Connecter"}
          </Button>
        </div>
      </div>
    </IntegrationCard>
  );
}

// ── Pudu Cloud Card ───────────────────────────────────────────────────────────

function PuduCard() {
  const { toast } = useToast();
  const [launching, setLaunching] = useState(false);
  const [diagResult, setDiagResult] = useState<any | null>(null);
  const [diagLoading, setDiagLoading] = useState(false);
  const [diagOpen, setDiagOpen] = useState(false);
  const [agentDiagResult, setAgentDiagResult] = useState<any | null>(null);
  const [agentDiagLoading, setAgentDiagLoading] = useState(false);
  const [agentDiagOpen, setAgentDiagOpen] = useState(false);
  const [agentCodeInput, setAgentCodeInput] = useState("FR0039");
  const [hmacResult, setHmacResult] = useState<any | null>(null);
  const [hmacLoading, setHmacLoading] = useState(false);
  const [hmacOpen, setHmacOpen] = useState(false);

  const { data: puduStatus, isLoading: statusLoading } = useQuery<PuduStatus>({
    queryKey: ["/api/pudu/status"],
    queryFn: () => authFetch("/api/pudu/status").then(r => r.json()),
  });

  // ── Polling statut sync (toutes les 3s si en cours) ──────────────────────
  const { data: syncStatus, refetch: refetchSyncStatus } = useQuery<PuduSyncStatus>({
    queryKey: ["/api/pudu/sync-status"],
    queryFn: () => authFetch("/api/pudu/sync-status").then(r => r.json()),
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === "running" ? 3000 : false;
    },
  });

  const isRunning = syncStatus?.status === "running" || launching;

  const handleSync = async () => {
    if (isRunning) return;
    setLaunching(true);
    try {
      const res = await authFetch("/api/pudu/sync-robots", { method: "POST" });
      const data = await res.json();
      if (res.status === 409) {
        toast({ title: "Synchro déjà en cours", description: data.message, variant: "destructive" });
      } else if (!res.ok) {
        throw new Error(data.message || `Erreur ${res.status}`);
      } else {
        toast({ title: "Synchronisation lancée", description: "Traitement en arrière-plan…" });
      }
      await refetchSyncStatus();
    } catch (e: any) {
      toast({ title: "Erreur Pudu", description: e.message, variant: "destructive" });
    } finally {
      setLaunching(false);
    }
  };

  const runDiagnostic = async () => {
    setDiagLoading(true);
    setDiagOpen(true);
    setDiagResult(null);
    try {
      const res = await authFetch("/api/pudu/diagnostic");
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || data.message || `Erreur ${res.status}`);
      setDiagResult(data);
    } catch (e: any) {
      setDiagResult({ _error: e.message });
      toast({ title: "Erreur diagnostic", description: e.message, variant: "destructive" });
    } finally {
      setDiagLoading(false);
    }
  };

  const runAgentDiagnostic = async () => {
    setAgentDiagLoading(true);
    setAgentDiagOpen(true);
    setAgentDiagResult(null);
    try {
      const res = await authFetch("/api/pudu/diagnostic-agents", { method: "POST" });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || data.message || `Erreur ${res.status}`);
      setAgentDiagResult(data);
    } catch (e: any) {
      setAgentDiagResult({ _error: e.message });
      toast({ title: "Erreur diagnostic agents", description: e.message, variant: "destructive" });
    } finally {
      setAgentDiagLoading(false);
    }
  };

  const runHmacTest = async () => {
    setHmacLoading(true);
    setHmacOpen(true);
    setHmacResult(null);
    try {
      const res = await authFetch("/api/pudu/hmac-test", { method: "POST" });
      const data = await res.json();
      setHmacResult(data);
      if (data.ok) {
        toast({ title: "Test HMAC réussi", description: `HTTP ${data.httpStatus} — authentification HMAC-SHA1 validée` });
      } else {
        toast({ title: "Test HMAC échoué", description: `HTTP ${data.httpStatus}`, variant: "destructive" });
      }
    } catch (e: any) {
      setHmacResult({ error: e.message });
      toast({ title: "Erreur test HMAC", description: e.message, variant: "destructive" });
    } finally {
      setHmacLoading(false);
    }
  };

  const connected = puduStatus?.connected ?? false;

  return (
    <IntegrationCard>
      <div className="p-6 space-y-5">
        {/* Header */}
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
              <Bot className="w-6 h-6 text-primary" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-semibold text-base">Pudu Cloud</h3>
                {!statusLoading && <StatusBadge connected={connected} />}
              </div>
              <p className="text-sm text-muted-foreground mt-0.5">
                Synchronisation des robots installés chez les clients
              </p>
            </div>
          </div>
        </div>

        {/* ── Bloc de progression / résultat ─────────────────────────────── */}
        {syncStatus && syncStatus.status !== "never" && (
          <div className={`rounded-lg border p-4 space-y-3 ${
            syncStatus.status === "running"
              ? "bg-blue-500/5 border-blue-200 dark:border-blue-800/50"
              : syncStatus.status === "completed"
              ? "bg-green-500/5 border-green-200 dark:border-green-800/50"
              : "bg-destructive/5 border-destructive/20"
          }`} data-testid="pudu-sync-status-block">

            {/* Titre + badge statut */}
            <div className="flex items-center gap-2">
              {syncStatus.status === "running" && <Loader2 className="w-4 h-4 animate-spin text-blue-600" />}
              {syncStatus.status === "completed" && <CheckCircle2 className="w-4 h-4 text-green-600" />}
              {syncStatus.status === "error" && <AlertTriangle className="w-4 h-4 text-destructive" />}
              <span className="text-sm font-medium">
                {syncStatus.status === "running" && "Synchronisation en cours…"}
                {syncStatus.status === "completed" && "Synchronisation terminée"}
                {syncStatus.status === "error" && "Erreur de synchronisation"}
              </span>
              {syncStatus.started_at && (
                <span className="ml-auto text-xs text-muted-foreground">
                  {new Date(syncStatus.started_at).toLocaleTimeString("fr-FR")}
                </span>
              )}
            </div>

            {/* Métriques en temps réel */}
            {(syncStatus.status === "running" || syncStatus.status === "completed") && (
              <div className="grid grid-cols-3 gap-2">
                <div className="bg-background/60 rounded-lg p-2 text-center">
                  <p className="text-lg font-bold tabular-nums">{syncStatus.shops_synced ?? 0}</p>
                  <p className="text-[11px] text-muted-foreground">Magasins</p>
                </div>
                <div className="bg-background/60 rounded-lg p-2 text-center">
                  <p className="text-lg font-bold tabular-nums text-blue-600 dark:text-blue-400">{syncStatus.robots_synced ?? 0}</p>
                  <p className="text-[11px] text-muted-foreground">Robots</p>
                </div>
                <div className="bg-background/60 rounded-lg p-2 text-center">
                  <p className="text-lg font-bold tabular-nums text-purple-600 dark:text-purple-400">{syncStatus.resellers_matched ?? 0}</p>
                  <p className="text-[11px] text-muted-foreground">Revendeurs</p>
                </div>
              </div>
            )}

            {/* Barre de progression animée si en cours */}
            {syncStatus.status === "running" && (
              <div className="w-full h-1.5 bg-blue-100 dark:bg-blue-900/30 rounded-full overflow-hidden">
                <div className="h-full bg-blue-500 rounded-full animate-pulse" style={{ width: "60%" }} />
              </div>
            )}

            {/* Message d'erreur */}
            {syncStatus.status === "error" && syncStatus.error_message && (
              <p className="text-xs text-destructive">{syncStatus.error_message}</p>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-wrap gap-2">
          <Button
            onClick={handleSync}
            disabled={isRunning}
            data-testid="button-pudu-sync"
            className="gap-2"
          >
            {isRunning ? (
              <><Loader2 className="w-4 h-4 animate-spin" />Synchronisation en cours…</>
            ) : (
              <><RefreshCw className="w-4 h-4" />Synchroniser les robots</>
            )}
          </Button>
          <Button
            variant="outline"
            onClick={runDiagnostic}
            disabled={diagLoading}
            data-testid="button-pudu-diagnostic"
            className="gap-2"
          >
            {diagLoading ? (
              <><Loader2 className="w-4 h-4 animate-spin" />Diagnostic en cours…</>
            ) : (
              <><Activity className="w-4 h-4" />Lancer le diagnostic API</>
            )}
          </Button>
          <Button
            variant="outline"
            onClick={runHmacTest}
            disabled={hmacLoading}
            data-testid="button-pudu-hmac-test"
            className="gap-2 border-violet-300 dark:border-violet-700 text-violet-700 dark:text-violet-400 hover:bg-violet-50 dark:hover:bg-violet-950/30"
          >
            {hmacLoading ? (
              <><Loader2 className="w-4 h-4 animate-spin" />Test HMAC en cours…</>
            ) : (
              <><KeyRound className="w-4 h-4" />Tester HMAC</>
            )}
          </Button>
        </div>

        {/* Test agents subalternes */}
        <div className="border rounded-xl p-4 space-y-3 bg-amber-500/5 border-amber-200 dark:border-amber-800/50">
          <div className="flex items-center gap-2">
            <Bot className="w-4 h-4 text-amber-600 dark:text-amber-400" />
            <p className="text-sm font-semibold text-amber-800 dark:text-amber-300">Test API agents subalternes</p>
          </div>
          <p className="text-xs text-muted-foreground">
            Teste 5 variantes d'endpoints Pudu Cloud pour récupérer les magasins d'un agent subalterne (revendeur).
            Indiquez le code agent Pudu — ex: <code className="font-mono bg-muted px-1 rounded">FR0039</code> pour Kargo.
          </p>
          <div className="flex items-center gap-2">
            <input
              value={agentCodeInput}
              onChange={e => setAgentCodeInput(e.target.value)}
              placeholder="Code agent (ex: FR0039)"
              className="flex-1 px-3 py-1.5 text-sm rounded-lg border border-border bg-background text-foreground font-mono focus:outline-none focus:ring-2 focus:ring-amber-400/40"
              data-testid="input-pudu-agent-code"
            />
            <Button
              variant="outline"
              onClick={runAgentDiagnostic}
              disabled={agentDiagLoading}
              data-testid="button-pudu-agent-diagnostic"
              className="gap-2 border-amber-300 dark:border-amber-700 text-amber-700 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-950/30 whitespace-nowrap"
            >
              {agentDiagLoading ? (
                <><Loader2 className="w-4 h-4 animate-spin" />Test en cours…</>
              ) : (
                <><Activity className="w-4 h-4" />Tester API Pudu agents</>
              )}
            </Button>
          </div>
        </div>

        {/* HMAC test result panel */}
        {hmacOpen && (
          <div className="border rounded-xl overflow-hidden" data-testid="panel-pudu-hmac">
            <button
              onClick={() => setHmacOpen(o => !o)}
              className="w-full flex items-center justify-between px-4 py-3 bg-violet-500/10 hover:bg-violet-500/15 text-sm font-medium transition-colors"
            >
              <span className="flex items-center gap-2">
                <KeyRound className="w-4 h-4 text-violet-600 dark:text-violet-400" />
                Résultat test HMAC-SHA1
                {hmacResult && (
                  <span className={`ml-2 px-1.5 py-0.5 rounded text-xs font-bold ${hmacResult.ok ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300' : 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'}`}>
                    {hmacResult.ok ? `HTTP ${hmacResult.httpStatus} ✓` : hmacResult.httpStatus ? `HTTP ${hmacResult.httpStatus} ✗` : 'ERREUR'}
                  </span>
                )}
              </span>
              {hmacOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>

            {hmacLoading && (
              <div className="flex items-center gap-2 px-4 py-3 text-sm text-muted-foreground">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Test HMAC en cours…</span>
              </div>
            )}

            {hmacResult && !hmacLoading && (
              <div className="divide-y text-xs font-mono">
                {hmacResult.error && (
                  <div className="flex items-start gap-2 px-4 py-3 bg-destructive/10 text-destructive">
                    <XCircle className="w-4 h-4 shrink-0 mt-0.5" />
                    <span className="break-all">{hmacResult.error}</span>
                  </div>
                )}

                {/* Signing info */}
                {hmacResult.signingInfo && (
                  <div className="px-4 py-3 space-y-2 bg-secondary/20">
                    <p className="font-semibold text-xs text-foreground not-italic mb-2">Informations de signature</p>
                    <div className="space-y-1.5">
                      <p className="text-muted-foreground">Algorithme : <span className="text-violet-600 dark:text-violet-400 font-bold">hmac-sha1</span></p>
                      <p className="text-muted-foreground">x-date : <span className="text-foreground">{hmacResult.signingInfo.xDate}</span></p>
                      <p className="text-muted-foreground">Path normalisé : <span className="text-foreground break-all">{hmacResult.signingInfo.normalizedPath}</span></p>
                      <div>
                        <p className="text-muted-foreground mb-1">Signing string :</p>
                        <pre className="bg-muted/60 rounded p-2 text-xs whitespace-pre-wrap break-all text-foreground leading-relaxed">
                          {hmacResult.signingInfo.signingString}
                        </pre>
                      </div>
                      <div>
                        <p className="text-muted-foreground mb-1">Authorization :</p>
                        <pre className="bg-muted/60 rounded p-2 text-xs whitespace-pre-wrap break-all text-foreground leading-relaxed">
                          {hmacResult.signingInfo.authorization}
                        </pre>
                      </div>
                    </div>
                  </div>
                )}

                {/* HTTP response */}
                <div className={`px-4 py-3 space-y-1 ${hmacResult.ok ? 'bg-green-500/5' : 'bg-red-500/5'}`}>
                  <p className="font-semibold text-xs text-foreground not-italic">
                    Réponse HTTP
                    {hmacResult.endpoint && <span className="ml-2 font-normal text-muted-foreground">{hmacResult.endpoint}</span>}
                  </p>
                  <p className={hmacResult.ok ? 'text-green-700 dark:text-green-400' : 'text-red-600 dark:text-red-400'}>
                    Status : <span className="font-bold">{hmacResult.httpStatus || '—'}</span>
                    {hmacResult.ok ? ' ✓ Succès' : ' ✗ Échec'}
                  </p>
                  {hmacResult.raw && (
                    <div>
                      <p className="text-muted-foreground mb-1">Réponse brute :</p>
                      <pre className="bg-muted/60 rounded p-2 text-xs whitespace-pre-wrap break-all text-foreground leading-relaxed max-h-64 overflow-auto">
                        {hmacResult.parsed
                          ? JSON.stringify(hmacResult.parsed, null, 2).slice(0, 1500)
                          : hmacResult.raw}
                      </pre>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Diagnostic result panel */}
        {diagResult && (
          <div className="border rounded-xl overflow-hidden" data-testid="panel-pudu-diagnostic">
            <button
              onClick={() => setDiagOpen(o => !o)}
              className="w-full flex items-center justify-between px-4 py-3 bg-secondary/40 hover:bg-secondary/60 text-sm font-medium transition-colors"
            >
              <span className="flex items-center gap-2">
                <Activity className="w-4 h-4 text-primary" />
                Résultats du diagnostic Pudu API
              </span>
              {diagOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>

            {diagOpen && (
              <div className="divide-y text-xs font-mono">
                {/* Error globale */}
                {diagResult._error && (
                  <div className="flex items-center gap-2 px-4 py-3 bg-destructive/10 text-destructive">
                    <XCircle className="w-4 h-4 shrink-0" />
                    <span>{diagResult._error}</span>
                  </div>
                )}

                {/* Summary */}
                {diagResult.summary && (
                  <div className="px-4 py-3 bg-primary/5 space-y-1">
                    <p className="font-semibold text-xs text-foreground not-italic">Résumé</p>
                    <p className="text-muted-foreground">Magasins directs : <span className="text-foreground font-bold">{diagResult.summary.storesCount}</span></p>
                    <p className="text-muted-foreground">Robots (échantillon) : <span className="text-foreground font-bold">{diagResult.summary.robotsSampleCount}</span> — {diagResult.summary.note}</p>
                    {diagResult.storeFields?.length > 0 && (
                      <p className="text-muted-foreground">Champs store : <span className="text-foreground">{diagResult.storeFields.join(', ')}</span></p>
                    )}
                  </div>
                )}

                {/* Agent endpoints */}
                {diagResult.agentEndpointsExploration && (
                  <>
                    <div className="px-4 py-2 bg-secondary/20">
                      <p className="font-semibold text-xs text-foreground">Endpoints agents subalternes</p>
                    </div>
                    {Object.entries(diagResult.agentEndpointsExploration).map(([ep, result]: [string, any]) => {
                      const ok = result.ok && result.status === 200;
                      const hasData = result.data && !result.error;
                      const dataStr = result.raw ? result.raw.slice(0, 120) : result.error?.slice(0, 120) ?? '';
                      return (
                        <div key={ep} className={`flex items-start gap-3 px-4 py-2.5 ${ok ? 'bg-green-500/5' : 'bg-destructive/5'}`}>
                          <span className="shrink-0 text-base leading-tight">{ok ? '✅' : '❌'}</span>
                          <div className="min-w-0 flex-1 space-y-0.5">
                            <p className={`font-semibold truncate ${ok ? 'text-green-700 dark:text-green-400' : 'text-muted-foreground'}`}>
                              {ep}
                              {result.status && <span className="ml-2 font-normal opacity-60">HTTP {result.status}</span>}
                            </p>
                            {dataStr && (
                              <p className="text-muted-foreground break-all opacity-80 leading-relaxed">{dataStr}</p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </>
                )}

                {/* Robots sample */}
                {diagResult.robotsSample?.length > 0 && (
                  <>
                    <div className="px-4 py-2 bg-secondary/20">
                      <p className="font-semibold text-xs text-foreground">Échantillon robots (3 premiers magasins)</p>
                    </div>
                    {diagResult.robotsSample.map((s: any, i: number) => (
                      <div key={i} className="px-4 py-2.5 space-y-0.5">
                        <p className="text-foreground font-semibold">{s.storeName} <span className="font-normal text-muted-foreground">({s.robotsCount} robot{s.robotsCount > 1 ? 's' : ''})</span></p>
                        {s.firstRobot && (
                          <p className="text-muted-foreground truncate">Ex: {JSON.stringify(s.firstRobot).slice(0, 100)}</p>
                        )}
                      </div>
                    ))}
                  </>
                )}
              </div>
            )}
          </div>
        )}

        {/* Agent diagnostic result panel */}
        {agentDiagResult && (
          <div className="border rounded-xl overflow-hidden" data-testid="panel-pudu-agent-diagnostic">
            <button
              onClick={() => setAgentDiagOpen(o => !o)}
              className="w-full flex items-center justify-between px-4 py-3 bg-amber-500/10 hover:bg-amber-500/15 text-sm font-medium transition-colors"
            >
              <span className="flex items-center gap-2 text-amber-800 dark:text-amber-300">
                <Bot className="w-4 h-4" />
                Résultats — fetchPuduStores()
                {agentDiagResult.success && (
                  <span className="text-[10px] font-bold bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400 px-1.5 py-0.5 rounded">
                    {agentDiagResult.totalStores} magasin{agentDiagResult.totalStores > 1 ? "s" : ""}
                  </span>
                )}
                {agentDiagResult.success === false && (
                  <span className="text-[10px] font-bold bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400 px-1.5 py-0.5 rounded">ERREUR</span>
                )}
              </span>
              {agentDiagOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>

            {agentDiagOpen && (
              <div className="divide-y text-xs font-mono">
                {/* Erreur */}
                {agentDiagResult.error && (
                  <div className="flex items-center gap-2 px-4 py-3 bg-destructive/10 text-destructive">
                    <XCircle className="w-4 h-4 shrink-0" />
                    <span className="break-all">{agentDiagResult.error}</span>
                  </div>
                )}

                {/* Succès : structure des champs */}
                {agentDiagResult.success && (
                  <>
                    <div className="px-4 py-2 bg-secondary/20">
                      <p className="font-semibold text-xs text-foreground not-italic">
                        fetchPuduStores() — {agentDiagResult.totalStores} magasin(s) retourné(s)
                      </p>
                    </div>

                    {agentDiagResult.allFields?.length > 0 && (
                      <div className="px-4 py-3 space-y-1">
                        <p className="text-foreground font-semibold not-italic text-xs">Tous les champs d'un store :</p>
                        <p className="text-muted-foreground break-all">{agentDiagResult.allFields.join(' · ')}</p>
                        {agentDiagResult.agentRelatedFields?.length > 0 ? (
                          <p className="text-amber-700 dark:text-amber-400 font-bold not-italic">
                            ⚡ Champs agent/owner/reseller détectés : {agentDiagResult.agentRelatedFields.join(', ')}
                          </p>
                        ) : (
                          <p className="text-muted-foreground italic not-italic">
                            Aucun champ agent/reseller/owner/channel détecté dans la structure store.
                          </p>
                        )}
                      </div>
                    )}

                    {agentDiagResult.firstThreeStores?.map((store: any, i: number) => (
                      <div key={i} className="px-4 py-2.5 space-y-0.5">
                        <p className="text-foreground font-semibold not-italic">
                          Store {i + 1} — {store.storeName ?? store.store_name ?? `#${store.storeId ?? store.id}`}
                        </p>
                        <p className="text-muted-foreground break-all whitespace-pre-wrap">
                          {JSON.stringify(store, null, 2).slice(0, 600)}
                        </p>
                      </div>
                    ))}
                  </>
                )}
              </div>
            )}
          </div>
        )}

      </div>
    </IntegrationCard>
  );
}

// ── Clearbit Logo Card ────────────────────────────────────────────────────────

interface ClearbitSyncResult {
  clearbitCount: number;
  faviconCount: number;
  ignoredCount: number;
  failedCount: number;
}

function ClearbitCard() {
  const { toast } = useToast();
  const [syncResult, setSyncResult] = useState<ClearbitSyncResult | null>(null);

  const syncMutation = useMutation({
    mutationFn: async () => {
      const res = await authFetch("/api/clearbit/sync", { method: "POST" });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || `Erreur ${res.status}`);
      return data as ClearbitSyncResult;
    },
    onSuccess: (data) => {
      setSyncResult(data);
      const total = data.clearbitCount + data.faviconCount;
      toast({
        title: "Import logos terminé",
        description: `${total} logo${total !== 1 ? "s" : ""} récupéré${total !== 1 ? "s" : ""} (${data.clearbitCount} Clearbit, ${data.faviconCount} favicon)`,
      });
    },
    onError: (err: any) => toast({ title: "Erreur", description: err.message, variant: "destructive" }),
  });

  return (
    <IntegrationCard>
      <div className="p-6 space-y-5">
        {/* Header */}
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-violet-500/10 flex items-center justify-center shrink-0">
              <Image className="w-6 h-6 text-violet-600" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-semibold text-base">Clearbit Logo API</h3>
                <Badge className="gap-1 bg-green-100 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-400 dark:border-green-800 text-xs">
                  Actif · Gratuit
                </Badge>
              </div>
              <p className="text-sm text-muted-foreground mt-0.5">
                Récupération automatique des logos clients et revendeurs depuis leur domaine e-mail
              </p>
            </div>
          </div>
        </div>

        <p className="text-sm text-muted-foreground">
          Pour chaque fiche sans logo, le domaine est extrait de l'e-mail de contact.
          Le logo est cherché sur Clearbit, puis sur Google Favicon en fallback.
          Les logos déjà renseignés manuellement ne sont pas écrasés.
        </p>

        {/* Sync result */}
        {syncResult && (
          <div className="border rounded-lg p-4 bg-green-50 dark:bg-green-950/20 space-y-3">
            <div className="flex items-center gap-2 text-green-700 dark:text-green-400 font-medium text-sm">
              <CheckCircle2 className="w-4 h-4" />
              Résultat de la synchronisation
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-center text-sm">
              <div>
                <p className="font-bold text-green-700 dark:text-green-400">{syncResult.clearbitCount}</p>
                <p className="text-xs text-muted-foreground">Clearbit</p>
              </div>
              <div>
                <p className="font-bold text-blue-600">{syncResult.faviconCount}</p>
                <p className="text-xs text-muted-foreground">Favicon</p>
              </div>
              <div>
                <p className="font-bold text-muted-foreground">{syncResult.ignoredCount}</p>
                <p className="text-xs text-muted-foreground">Ignorées</p>
              </div>
              <div>
                <p className="font-bold text-amber-600">{syncResult.failedCount}</p>
                <p className="text-xs text-muted-foreground">Échecs</p>
              </div>
            </div>
          </div>
        )}

        {/* Error */}
        {syncMutation.isError && (
          <div className="flex items-start gap-2 bg-destructive/10 border border-destructive/30 rounded-lg px-4 py-3">
            <AlertTriangle className="w-4 h-4 text-destructive shrink-0 mt-0.5" />
            <p className="text-sm text-destructive">{(syncMutation.error as any)?.message}</p>
          </div>
        )}

        {/* Action */}
        <Button
          onClick={() => syncMutation.mutate()}
          disabled={syncMutation.isPending}
          data-testid="button-clearbit-sync"
          variant="outline"
          className="gap-2"
        >
          {syncMutation.isPending ? (
            <><Loader2 className="w-4 h-4 animate-spin" />Récupération en cours…</>
          ) : (
            <><RefreshCw className="w-4 h-4" />Récupérer les logos manquants</>
          )}
        </Button>
      </div>
    </IntegrationCard>
  );
}

// ── Claude AI Card ────────────────────────────────────────────────────────────

interface AnthropicStatus {
  connected: boolean;
}

interface AnthropicTestResult {
  status: "ok" | "error";
  model?: string;
  message?: string;
}

const FONCTIONNALITES = [
  { label: "Résumé automatique des tickets", active: true },
  { label: "Résumé automatique des dossiers SAV", active: true },
  { label: "Rédaction de rapports d'intervention", active: false },
  { label: "Assistant devis intelligent", active: false },
  { label: "Identification pièce par photo", active: false },
];

function ClaudeCard() {
  const { toast } = useToast();
  const [testResult, setTestResult] = useState<AnthropicTestResult | null>(null);
  const [testing, setTesting] = useState(false);

  const { data: status, isLoading: statusLoading } = useQuery<AnthropicStatus>({
    queryKey: ["/api/anthropic/status"],
    queryFn: () => authFetch("/api/anthropic/status").then(r => r.json()),
  });

  const connected = status?.connected ?? false;

  async function runTest() {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await authFetch("/api/anthropic/test");
      const data: AnthropicTestResult = await res.json();
      setTestResult(data);
      if (data.status === "ok") {
        toast({ title: "Claude AI opérationnel", description: `Modèle : ${data.model}` });
      } else {
        toast({ title: "Erreur de connexion Claude", description: data.message, variant: "destructive" });
      }
    } catch (e: any) {
      const msg = e?.message ?? "Erreur réseau";
      setTestResult({ status: "error", message: msg });
      toast({ title: "Erreur", description: msg, variant: "destructive" });
    } finally {
      setTesting(false);
    }
  }

  return (
    <IntegrationCard>
      <div className="p-6 space-y-5">
        {/* Header */}
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-violet-500/10 flex items-center justify-center shrink-0">
              <Sparkles className="w-6 h-6 text-violet-600 dark:text-violet-400" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-semibold text-base">Claude AI — Anthropic</h3>
                {!statusLoading && (
                  connected ? (
                    <Badge className="gap-1 bg-green-100 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-400 dark:border-green-800">
                      <Wifi className="w-3 h-3" />
                      Connecté
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="gap-1 text-muted-foreground">
                      <WifiOff className="w-3 h-3" />
                      Non configuré
                    </Badge>
                  )
                )}
              </div>
              <p className="text-sm text-muted-foreground mt-0.5">
                Résumé automatique de tickets et dossiers SAV, génération de rapports, assistant devis
              </p>
            </div>
          </div>
        </div>

        {/* Modèle */}
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-secondary/40 text-xs text-muted-foreground">
          <Sparkles className="w-3.5 h-3.5 text-violet-500 shrink-0" />
          <span>Modèle utilisé : <span className="font-mono font-medium text-foreground">claude-sonnet-4-20250514</span></span>
        </div>

        {/* Fonctionnalités actives */}
        <div>
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Fonctionnalités actives</p>
          <ul className="space-y-1.5">
            {FONCTIONNALITES.map((f) => (
              <li key={f.label} className="flex items-center gap-2 text-sm">
                {f.active ? (
                  <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400 shrink-0" />
                ) : (
                  <Clock className="w-4 h-4 text-muted-foreground/50 shrink-0" />
                )}
                <span className={f.active ? "text-foreground" : "text-muted-foreground"}>
                  {f.label}
                  {!f.active && <span className="ml-1.5 text-xs text-muted-foreground/60">— à venir</span>}
                </span>
              </li>
            ))}
          </ul>
        </div>

        {/* Résultat du test */}
        {testResult && (
          <div className={`flex items-start gap-3 rounded-lg px-4 py-3 text-sm ${
            testResult.status === "ok"
              ? "bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-800 text-green-800 dark:text-green-300"
              : "bg-destructive/10 border border-destructive/30 text-destructive"
          }`} data-testid="text-anthropic-test-result">
            {testResult.status === "ok" ? (
              <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" />
            ) : (
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
            )}
            <span>
              {testResult.status === "ok"
                ? `Connexion Claude AI opérationnelle — modèle ${testResult.model}`
                : testResult.message}
            </span>
          </div>
        )}

        {/* Action */}
        <Button
          variant="outline"
          onClick={runTest}
          disabled={testing || !connected}
          data-testid="button-anthropic-test"
          className="gap-2"
        >
          {testing ? (
            <><Loader2 className="w-4 h-4 animate-spin" />Test en cours…</>
          ) : (
            <><Sparkles className="w-4 h-4" />Tester la connexion</>
          )}
        </Button>
      </div>
    </IntegrationCard>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

function SectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider pt-2">
      {children}
    </p>
  );
}

export default function IntegrationsList() {
  return (
    <div className="space-y-6 p-6 max-w-3xl">
      {/* Page header */}
      <div>
        <div className="flex items-center gap-3 mb-1">
          <Plug className="w-6 h-6 text-primary" />
          <h1 className="text-3xl font-display font-bold" data-testid="text-page-title">
            Intégrations
          </h1>
        </div>
        <p className="text-muted-foreground ml-9">
          Connectez vos outils externes à la plateforme
        </p>
      </div>

      {/* ERP & Commandes */}
      <div className="space-y-3">
        <SectionHeading>ERP &amp; Commandes</SectionHeading>
        <ZohoCard />
        <ZohoBillingCard />
      </div>

      {/* Robotique */}
      <div className="space-y-3">
        <SectionHeading>Robotique</SectionHeading>
        <PuduCard />
      </div>

      {/* Données & Enrichissement */}
      <div className="space-y-3">
        <SectionHeading>Données &amp; Enrichissement</SectionHeading>
        <ClearbitCard />
      </div>

      {/* Intelligence Artificielle */}
      <div className="space-y-3">
        <SectionHeading>Intelligence Artificielle</SectionHeading>
        <ClaudeCard />
      </div>
    </div>
  );
}
